import { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { StatusBadge, PassTypeBadge, formatDate, formatDateTime } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { getMyRequests, createGatePass, getCourses, getGatePass } from "../lib/api";
import { Plus, FileText, Clock, CheckCircle2, XCircle, Eye, Printer, Loader2 } from "lucide-react";

const StudentDashboard = () => {
  return (
    <DashboardLayout title="Student Portal">
      <Routes>
        <Route index element={<StudentHome />} />
        <Route path="new" element={<NewGatePass />} />
      </Routes>
    </DashboardLayout>
  );
};

const StudentHome = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPass, setSelectedPass] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const { data } = await getMyRequests();
      setRequests(data);
    } catch (error) {
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status.startsWith("pending")).length,
    approved: requests.filter(r => r.status === "approved" || r.status === "gate_verified").length,
    rejected: requests.filter(r => r.status.startsWith("rejected")).length,
  };

  return (
    <div className="space-y-8" data-testid="student-dashboard">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Requests</p>
                <p className="text-2xl font-semibold text-slate-900">{stats.total}</p>
              </div>
              <FileText className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-semibold text-amber-600">{stats.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Approved</p>
                <p className="text-2xl font-semibold text-emerald-600">{stats.approved}</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Rejected</p>
                <p className="text-2xl font-semibold text-red-600">{stats.rejected}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* New Request Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-slate-900">My Gate Pass Requests</h2>
        <Button onClick={() => navigate("/student/new")} data-testid="new-request-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Request
        </Button>
      </div>

      {/* Requests Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No gate pass requests yet</p>
              <Button 
                variant="link" 
                onClick={() => navigate("/student/new")}
                className="mt-2"
              >
                Create your first request
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Request No.</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Exit Date</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((req) => (
                  <TableRow key={req.id} data-testid={`request-row-${req.id}`}>
                    <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                    <TableCell><PassTypeBadge type={req.pass_type} /></TableCell>
                    <TableCell>{formatDate(req.exit_date)}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{req.destination}</TableCell>
                    <TableCell><StatusBadge status={req.status} /></TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedPass(req)}
                          data-testid={`view-btn-${req.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {(req.status === "approved" || req.status === "gate_verified") && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/print/${req.id}`)}
                            data-testid={`print-btn-${req.id}`}
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Pass Dialog */}
      <GatePassDetailDialog 
        pass={selectedPass} 
        onClose={() => setSelectedPass(null)} 
      />
    </div>
  );
};

const NewGatePass = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    pass_type: "",
    reason: "",
    exit_date: "",
    exit_time: "",
    expected_return_date: "",
    expected_return_time: "",
    destination: "",
    emergency_contact: user?.phone || "",
    student_remarks: "",
  });

  useEffect(() => {
    getCourses().then(({ data }) => setCourses(data)).catch(() => {});
  }, []);

  const courseName = courses.find(c => c.id === user?.course_id)?.name || user?.course_id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.pass_type || !formData.reason || !formData.exit_date || 
        !formData.exit_time || !formData.expected_return_date || 
        !formData.expected_return_time || !formData.destination) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      await createGatePass(formData);
      toast.success("Gate pass request submitted successfully!");
      navigate("/student");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="new-gate-pass-form">
      <Button 
        variant="ghost" 
        onClick={() => navigate("/student")}
        className="mb-4"
      >
        &larr; Back to Dashboard
      </Button>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>New Gate Pass Request</CardTitle>
          <CardDescription>Fill in the details for your gate pass request</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Student Info (Read-only) */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-slate-500 text-xs">Student Name</Label>
                <p className="font-medium">{user?.name}</p>
              </div>
              <div>
                <Label className="text-slate-500 text-xs">Enrollment No.</Label>
                <p className="font-medium">{user?.enrollment_number || "-"}</p>
              </div>
              <div>
                <Label className="text-slate-500 text-xs">Course</Label>
                <p className="font-medium">{courseName} - Year {user?.year}</p>
              </div>
              <div>
                <Label className="text-slate-500 text-xs">Hostel Status</Label>
                <p className="font-medium capitalize">{user?.hostel_status?.replace("_", " ") || "Hostel"}</p>
              </div>
            </div>

            {/* Pass Type */}
            <div className="space-y-2">
              <Label htmlFor="pass_type">Pass Type *</Label>
              <Select 
                value={formData.pass_type} 
                onValueChange={(v) => setFormData({...formData, pass_type: v})}
              >
                <SelectTrigger data-testid="pass-type-select">
                  <SelectValue placeholder="Select pass type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short_leave">Short Leave</SelectItem>
                  <SelectItem value="day_pass">Day Pass</SelectItem>
                  <SelectItem value="emergency_leave">Emergency Leave</SelectItem>
                  <SelectItem value="home_visit">Home Visit</SelectItem>
                  <SelectItem value="medical_leave">Medical Leave</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">Reason for Leave *</Label>
              <Textarea
                id="reason"
                placeholder="Enter the reason for your gate pass request"
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                data-testid="reason-input"
                rows={3}
              />
            </div>

            {/* Exit Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="exit_date">Exit Date *</Label>
                <Input
                  id="exit_date"
                  type="date"
                  value={formData.exit_date}
                  onChange={(e) => setFormData({...formData, exit_date: e.target.value})}
                  data-testid="exit-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exit_time">Exit Time *</Label>
                <Input
                  id="exit_time"
                  type="time"
                  value={formData.exit_time}
                  onChange={(e) => setFormData({...formData, exit_time: e.target.value})}
                  data-testid="exit-time-input"
                />
              </div>
            </div>

            {/* Return Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expected_return_date">Expected Return Date *</Label>
                <Input
                  id="expected_return_date"
                  type="date"
                  value={formData.expected_return_date}
                  onChange={(e) => setFormData({...formData, expected_return_date: e.target.value})}
                  data-testid="return-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expected_return_time">Expected Return Time *</Label>
                <Input
                  id="expected_return_time"
                  type="time"
                  value={formData.expected_return_time}
                  onChange={(e) => setFormData({...formData, expected_return_time: e.target.value})}
                  data-testid="return-time-input"
                />
              </div>
            </div>

            {/* Destination */}
            <div className="space-y-2">
              <Label htmlFor="destination">Destination / Address *</Label>
              <Input
                id="destination"
                placeholder="Enter your destination address"
                value={formData.destination}
                onChange={(e) => setFormData({...formData, destination: e.target.value})}
                data-testid="destination-input"
              />
            </div>

            {/* Emergency Contact */}
            <div className="space-y-2">
              <Label htmlFor="emergency_contact">Emergency Contact Number</Label>
              <Input
                id="emergency_contact"
                placeholder="Enter emergency contact number"
                value={formData.emergency_contact}
                onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                data-testid="emergency-contact-input"
              />
            </div>

            {/* Student Remarks */}
            <div className="space-y-2">
              <Label htmlFor="student_remarks">Additional Remarks (Optional)</Label>
              <Textarea
                id="student_remarks"
                placeholder="Any additional information"
                value={formData.student_remarks}
                onChange={(e) => setFormData({...formData, student_remarks: e.target.value})}
                data-testid="remarks-input"
                rows={2}
              />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => navigate("/student")}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading} data-testid="submit-request-btn">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Request"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const GatePassDetailDialog = ({ pass, onClose }) => {
  if (!pass) return null;

  return (
    <Dialog open={!!pass} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gate Pass Details</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Header Info */}
          <div className="flex justify-between items-start">
            <div>
              <p className="font-mono text-sm text-slate-500">{pass.request_number}</p>
              <h3 className="text-lg font-semibold">{pass.student_name}</h3>
            </div>
            <StatusBadge status={pass.status} />
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Pass Type</p>
              <p className="font-medium capitalize">{pass.pass_type?.replace("_", " ")}</p>
            </div>
            <div>
              <p className="text-slate-500">Course / Year</p>
              <p className="font-medium">{pass.course_name} - Year {pass.year}</p>
            </div>
            <div>
              <p className="text-slate-500">Exit Date & Time</p>
              <p className="font-medium">{formatDate(pass.exit_date)} at {pass.exit_time}</p>
            </div>
            <div>
              <p className="text-slate-500">Return Date & Time</p>
              <p className="font-medium">{formatDate(pass.expected_return_date)} at {pass.expected_return_time}</p>
            </div>
            <div className="col-span-2">
              <p className="text-slate-500">Destination</p>
              <p className="font-medium">{pass.destination}</p>
            </div>
            <div className="col-span-2">
              <p className="text-slate-500">Reason</p>
              <p className="font-medium">{pass.reason}</p>
            </div>
          </div>

          {/* Approval History */}
          {pass.approval_history && pass.approval_history.length > 0 && (
            <div>
              <h4 className="font-semibold mb-3">Approval History</h4>
              <div className="space-y-3">
                {pass.approval_history.map((item, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${
                      item.action === "approve" 
                        ? "bg-emerald-50 border-emerald-200" 
                        : "bg-red-50 border-red-200"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{item.approver_name}</p>
                        <p className="text-xs text-slate-500 capitalize">
                          {item.stage?.replace(/_/g, " ")}
                          {item.acting_as && ` (Acting as ${item.acting_as})`}
                        </p>
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded ${
                        item.action === "approve" 
                          ? "bg-emerald-100 text-emerald-700" 
                          : "bg-red-100 text-red-700"
                      }`}>
                        {item.action === "approve" ? "Approved" : "Rejected"}
                      </span>
                    </div>
                    {item.remarks && <p className="text-sm mt-2">Remarks: {item.remarks}</p>}
                    {item.rejection_reason && (
                      <p className="text-sm mt-2 text-red-600">Reason: {item.rejection_reason}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-2">{formatDateTime(item.timestamp)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Parent Call Data */}
          {pass.parent_call_data && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold mb-2">Parent Call Details</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-slate-500">Called On</p>
                  <p className="font-medium">{pass.parent_call_data.call_date} at {pass.parent_call_data.call_time}</p>
                </div>
                <div>
                  <p className="text-slate-500">Received By</p>
                  <p className="font-medium">{pass.parent_call_data.who_received} ({pass.parent_call_data.relation_with_student})</p>
                </div>
                <div className="col-span-2">
                  <p className="text-slate-500">Parent Response</p>
                  <p className="font-medium">{pass.parent_call_data.parent_response}</p>
                </div>
              </div>
            </div>
          )}

          {/* Gate Verification */}
          {pass.gate_verification && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <h4 className="font-semibold mb-2">Gate Verification</h4>
              <div className="text-sm">
                <p><span className="text-slate-500">Verified By:</span> {pass.gate_verification.operator_name}</p>
                <p><span className="text-slate-500">Time:</span> {formatDateTime(pass.gate_verification.verification_time)}</p>
                {pass.gate_verification.remarks && (
                  <p><span className="text-slate-500">Remarks:</span> {pass.gate_verification.remarks}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { GatePassDetailDialog };
export default StudentDashboard;
